<template>
  <div class="space-y-6">
    <!-- 메인 헤더 -->
    <div class="glass-card p-8">
      <div class="text-center">
        <div class="text-5xl mb-6">🎪</div>
        <h2 class="text-4xl font-bold text-gradient mb-8">
          로또 번호 생성
        </h2>
      </div>

      <!-- 통합 생성 버튼 -->
      <div class="text-center">
        <button
          @click="generateAllNumbers"
          :disabled="isGenerating"
          class="btn-primary px-12 py-5 text-xl font-bold rounded-2xl shadow-xl hover:shadow-2xl transition-all"
        >
          <span v-if="isGenerating" class="flex items-center justify-center">
            <svg class="animate-spin w-7 h-7 mr-3" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ currentStep }}
          </span>
          <span v-else class="flex items-center justify-center gap-3">
            ✨ 번호 생성하기
          </span>
        </button>
      </div>
    </div>

    <!-- 진행률 표시 -->
    <div v-if="isGenerating" class="glass-card p-6">
      <div class="flex items-center space-x-4">
        <div class="flex-1">
          <div class="flex justify-between text-sm mb-2">
            <span class="font-medium">{{ currentStep }}</span>
            <span class="font-bold text-blue-600">{{ Math.round(progress) }}%</span>
          </div>
          <div class="w-full bg-gray-200 rounded-full h-4">
            <div
              class="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 h-4 rounded-full transition-all duration-300"
              :style="{ width: progress + '%' }"
            ></div>
          </div>
        </div>
      </div>
    </div>

    <!-- 상단 광고 (번호 생성 전) -->
    <AdSenseUnit v-if="results.length === 0" ad-slot="5075083572" />

    <!-- Kakao AdFit 광고 영역 -->
    <div v-show="showAd && results.length > 0" class="glass-card p-4 bg-gray-50">
      <div class="text-center">
        <div class="text-xs text-gray-500 mb-2">광고</div>
        <div
          ref="adContainer"
          :key="adKey"
          class="adfit-container w-full"
        >
          <!-- 광고 엘리먼트는 JavaScript에서 동적 생성 -->
        </div>
      </div>
    </div>

    <!-- 결과 표시 - 하나의 카드에 모든 게임 표시 -->
    <div v-if="results.length > 0">
      <!-- 하나의 큰 카드 -->
      <div class="glass-card p-8 bg-white">
        <div
          v-for="(result, index) in results"
          :key="index"
          :class="['py-6', index < results.length - 1 ? 'border-b-2 border-gray-100' : '']"
        >
          <!-- 게임 번호와 생성 방식 -->
          <div class="flex items-center justify-between mb-4">
            <span class="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
              {{ getGameLabel(index) }}
            </span>
            <span class="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
              {{ getGenerationMethod(result) }}
            </span>
          </div>

          <!-- 번호 표시 -->
          <div class="flex justify-center items-center gap-3 flex-wrap">
            <span
              v-for="number in result.numbers"
              :key="number"
              class="flex items-center justify-center w-16 h-16 rounded-full font-bold text-white text-2xl shadow-lg"
              :class="getNumberColor(number)"
            >
              {{ number }}
            </span>
          </div>
        </div>
      </div>

      <!-- 하단 광고 (번호 생성 후) -->
      <AdSenseUnit ad-slot="5130923897" />
    </div>

    <!-- 초기 상태 메시지 -->
    <div v-else class="glass-card p-20 text-center">
      <div class="text-9xl mb-6">🎰</div>
      <h3 class="text-3xl font-bold text-gray-400">위 버튼을 눌러 번호를 생성하세요</h3>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import {
  runAllEntertainmentAI,
  getAICharacters
} from '../utils/entertainmentAI.js';
import {
  PATTERN_TYPES,
  generatePattern,
  generateMegaPattern as generateMegaPatternUtil
} from '../utils/patternGenerator.js';
import { loadLottoData } from '../utils/lottoUtils.js';
import AdSenseUnit from './AdSenseUnit.vue';

// 반응형 상태
const results = ref([]);
const isGenerating = ref(false);
const currentStep = ref('');
const progress = ref(0);

// 광고 관련 상태
const showAd = ref(false);
const adContainer = ref(null);
const adKey = ref(0);
let adRetryCount = 0;
const maxAdRetries = 2;

// ============= 개발 환경 전용 로깅 유틸리티 =============
const devLog = (...args) => {
  if (import.meta.env.DEV) console.log(...args);
};

const devWarn = (...args) => {
  if (import.meta.env.DEV) console.warn(...args);
};

const devError = (...args) => {
  if (import.meta.env.DEV) console.error(...args);
};

// 광고 실패 처리 함수 (전역으로 등록)
const handleAdFail = (element) => {
  devLog("📺 광고 로딩 실패 - 재시도 검토 중...");
  devWarn("📺 가능한 원인: 광고 재고 부족, 일시적 네트워크 문제, 광고 차단기");

  if (adRetryCount < maxAdRetries) {
    adRetryCount++;
    devLog(`📺 광고 재시도 ${adRetryCount}/${maxAdRetries} 시작...`);

    setTimeout(() => {
      try {
        requestNewAd();
      } catch (error) {
        devError("📺 광고 재시도 중 오류:", error);
      }
    }, 2000);
  } else {
    devLog("📺 최대 재시도 횟수 초과 - 광고 영역을 숨김");
    showAd.value = false;
    adRetryCount = 0;
  }
};

// 전역 함수로 등록
if (typeof window !== "undefined") {
  window.handleAdFail = handleAdFail;
}

// 새로운 광고 요청 함수
const requestNewAd = () => {
  devLog("📺 새로운 광고 요청 시작...");

  const container = adContainer.value;
  if (!container) {
    devWarn("📺 광고 컨테이너를 찾을 수 없습니다.");
    return;
  }

  try {
    if (typeof window.adfit !== "undefined" && window.adfit.destroy) {
      window.adfit.destroy("DAN-mfWUgriWgqZmHAfx");
      devLog("📺 기존 AdFit 광고 단위 정리 완료");
    }
  } catch (error) {
    devLog("📺 기존 광고 정리 중 오류 (무시 가능):", error.message);
  }

  const existingAds = container.querySelectorAll(".kakao_ad_area");
  existingAds.forEach((ad) => ad.remove());

  const existingScripts = container.querySelectorAll('script[src*="kas/static/ba.min.js"]');
  existingScripts.forEach((script) => script.remove());

  container.innerHTML = '';
  devLog("📺 기존 광고 엘리먼트 정리 완료");

  const uniqueId = `adfit-${Date.now()}-${Math.random()}`;

  const newAd = document.createElement("ins");
  newAd.className = "kakao_ad_area";
  newAd.style.display = "none";
  newAd.style.width = "100%";
  newAd.setAttribute("data-ad-unit", "DAN-mfWUgriWgqZmHAfx");
  newAd.setAttribute("data-ad-width", "300");
  newAd.setAttribute("data-ad-height", "250");
  newAd.setAttribute("data-ad-onfail", "handleAdFail");
  newAd.id = uniqueId;

  devLog("📺 새로운 광고 엘리먼트 생성:", uniqueId);

  const script = document.createElement("script");
  script.type = "text/javascript";
  script.charset = "utf-8";
  script.src = "https://t1.daumcdn.net/kas/static/ba.min.js";
  script.async = true;

  container.appendChild(newAd);
  container.appendChild(script);

  devLog("📺 AdFit 스크립트 및 광고 엘리먼트 추가 완료");

  script.onload = () => {
    devLog("📺 AdFit 스크립트 로드 완료");
    setTimeout(() => {
      const adElement = document.getElementById(uniqueId);
      if (adElement && adElement.style.display !== "none") {
        devLog("📺 광고 로딩 성공!");
      } else {
        devLog("📺 광고 로딩 대기 중...");
      }
    }, 1000);
  };

  script.onerror = (error) => {
    devError("📺 AdFit 스크립트 로드 실패:", error);
    if (adRetryCount < maxAdRetries) {
      adRetryCount++;
      devLog(`📺 스크립트 로드 재시도 ${adRetryCount}/${maxAdRetries}`);
      setTimeout(() => requestNewAd(), 2000);
    } else {
      devLog("📺 최대 재시도 횟수 초과 - 광고 영역을 숨김");
      showAd.value = false;
      adRetryCount = 0;
    }
  };
};

// AdFit 스크립트 로딩 함수
const loadAdScript = () => {
  devLog("📺 AdFit 광고 로딩 시작...");
  requestNewAd();
};

// 광고 초기화 함수
const initializeAd = () => {
  devLog("📺 광고 초기화 시작...");

  adRetryCount = 0;
  devLog("📺 광고 재시도 카운터 리셋");

  if (typeof window !== "undefined" && window.adfit) {
    try {
      window.adfit.destroy("DAN-mfWUgriWgqZmHAfx");
      devLog("📺 기존 광고 단위 정리 완료");
    } catch (error) {
      devLog("📺 기존 광고 정리 중 오류 (무시 가능):", error.message);
    }
  }

  if (adContainer.value) {
    const existingAds = adContainer.value.querySelectorAll(".kakao_ad_area");
    existingAds.forEach((ad) => ad.remove());
    adContainer.value.innerHTML = '';
    devLog("📺 광고 컨테이너 정리 완료");
  }

  adKey.value = Date.now();
  showAd.value = true;

  devLog(`📺 새로운 광고 키 생성: ${adKey.value}`);

  nextTick(() => {
    devLog("📺 DOM 업데이트 완료, 광고 스크립트 로드 시작");
    loadAdScript();
  });
};

// 컴포넌트 마운트
onMounted(async () => {
  initializeAd();
});

// 컴포넌트 언마운트 시 정리
onUnmounted(() => {
  // 정리 작업
});

// 통합 번호 생성 함수 - AI 5게임 + 패턴 5게임
const generateAllNumbers = async () => {
  isGenerating.value = true;
  progress.value = 0;
  results.value = [];

  try {
    const historicalData = await loadLottoData();

    // 1단계: AI 5게임 생성 (50%)
    currentStep.value = '10개의 게임을 생성중입니다';
    progress.value = 10;

    const aiResults = await runAllEntertainmentAI(
      historicalData,
      (step, percent) => {
        currentStep.value = '10개의 게임을 생성중입니다';
        progress.value = percent * 0.5; // 50%까지만 사용
      }
    );

    progress.value = 50;
    currentStep.value = '10개의 게임을 생성중입니다';

    // 2단계: 패턴 5게임 생성 (50%)
    const patternResults = [];

    // 다양한 카테고리에서 패턴 선택
    const allPatternTypes = Object.values(PATTERN_TYPES).flatMap(category => category.patterns);
    const selectedPatterns = [];

    // 중복 없이 5개 패턴 선택
    while (selectedPatterns.length < 5 && selectedPatterns.length < allPatternTypes.length) {
      const randomPattern = allPatternTypes[Math.floor(Math.random() * allPatternTypes.length)];
      if (!selectedPatterns.includes(randomPattern)) {
        selectedPatterns.push(randomPattern);
      }
    }

    // 각 패턴으로 1게임씩 생성
    for (let i = 0; i < selectedPatterns.length; i++) {
      try {
        progress.value = 50 + ((i + 1) / selectedPatterns.length) * 40;
        const patternType = selectedPatterns[i];

        // generatePattern은 5게임 배열을 반환하므로, 그 중 첫 번째 게임만 사용
        const patternGames = generatePattern(patternType, { historicalData });

        // 배열에서 게임 인덱스에 해당하는 게임 선택 (다양성 보장)
        const gameIndex = i % patternGames.length;
        const game = patternGames[gameIndex];

        patternResults.push({
          ...game,
          gameNumber: i + 1, // 게임 번호 재설정
          probability: '1 / 8,145,060',
          disclaimer: '모든 번호 조합의 당첨 확률은 동일합니다'
        });
      } catch (error) {
        console.error(`패턴 생성 오류 (${selectedPatterns[i]}):`, error);
        // 폴백: 랜덤 번호 생성
        const fallbackNumbers = [];
        while (fallbackNumbers.length < 6) {
          const num = Math.floor(Math.random() * 45) + 1;
          if (!fallbackNumbers.includes(num)) fallbackNumbers.push(num);
        }
        patternResults.push({
          gameNumber: i + 1,
          numbers: fallbackNumbers.sort((a, b) => a - b),
          patternName: '랜덤 조합',
          description: '무작위로 생성된 번호',
          probability: '1 / 8,145,060',
          disclaimer: '모든 번호 조합의 당첨 확률은 동일합니다'
        });
      }
    }

    // 3단계: 결과 통합
    progress.value = 95;
    currentStep.value = '10개의 게임을 생성중입니다';

    // AI 결과와 패턴 결과 합치기
    results.value = [...aiResults, ...patternResults];

    progress.value = 100;

    // 광고 초기화
    setTimeout(() => {
      initializeAd();
    }, 100);

    setTimeout(() => {
      isGenerating.value = false;
      currentStep.value = '';
      progress.value = 0;
    }, 1000);

  } catch (error) {
    console.error('번호 생성 오류:', error);
    isGenerating.value = false;
    currentStep.value = '';
    progress.value = 0;
    alert('번호 생성 중 오류가 발생했어요! 다시 시도해주세요 😊');
  }
};

// 결과 초기화
const clearResults = () => {
  results.value = [];
};

// 유틸리티 함수들
const getGameLabel = (index) => {
  return (index + 1) + '게임'; // 1게임, 2게임, ...
};

const getGenerationMethod = (result) => {
  // AI 생성 방식인 경우
  if (result.aiCharacter && result.aiCharacter.name) {
    return 'AI';
  }

  // 패턴 생성 방식인 경우 - 더 직관적인 이름으로 변환
  if (result.patternName) {
    const patternNameMap = {
      '피보나치 수열': '피보나치',
      '소수의 신비': '소수 패턴',
      '제곱수 변주': '제곱수',
      '삼각수 패턴': '삼각수',
      '하트 그리기': '하트 모양',
      '별을 그리며': '별 모양',
      '대각선 미학': '대각선',
      '핫 넘버 셀렉션': '인기 번호',
      '콜드 넘버 셀렉션': '잠자는 번호',
      '완벽한 균형': '균형 조합',
      '역발상 전략': '역발상',
      '카오스 이론': '카오스',
      '양자 붕괴': '양자 랜덤',
      '우주의 속삭임': '우주 패턴',
      '자연의 선물': '자연수'
    };

    // 메가 콤보 패턴은 그대로 유지
    if (result.patternName.startsWith('메가 콤보')) {
      return result.patternName;
    }

    return patternNameMap[result.patternName] || result.patternName;
  }

  // 기본값
  return '랜덤 생성';
};

const getNumberColor = (number) => {
  if (number <= 10) return "bg-gradient-to-r from-yellow-400 to-amber-500";
  if (number <= 20) return "bg-gradient-to-r from-sky-400 to-blue-500";
  if (number <= 30) return "bg-gradient-to-r from-pink-400 to-rose-500";
  if (number <= 40) return "bg-gradient-to-r from-gray-500 to-gray-600";
  return "bg-gradient-to-r from-lime-400 to-green-500";
};

const getCurrentDate = () => {
  return new Date().toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric"
  });
};
</script>

<style scoped>
.text-gradient {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.glass-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  transition: all 0.3s ease;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 12px 35px rgba(102, 126, 234, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
  border: none;
  transition: all 0.3s ease;
}

.btn-secondary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 12px 35px rgba(240, 147, 251, 0.4);
}

/* AdFit 광고 스타일 */
.kakao_ad_area {
  @apply max-w-full;
}

/* AdFit 컨테이너 스타일 */
.adfit-container {
  @apply min-h-[250px] flex items-center justify-center;
}
</style>
